// src/components/article/MermaidChart.jsx
// Lazy-loaded client-side Mermaid renderer for React + Vite
// Import via: const MermaidChart = lazy(() => import('./MermaidChart'))

import mermaid from 'mermaid';
import { useEffect, useId, useRef, useState } from 'react';

mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    fontFamily: "'IBM Plex Mono', monospace",
    themeVariables: {
        primaryColor: '#D4A843',
        primaryTextColor: '#F0EDE6',
        primaryBorderColor: '#D4A843',
        lineColor: '#475569',
        secondaryColor: '#0F172A',
        tertiaryColor: '#1E293B',
        background: '#070D1A',
        nodeBorder: '#D4A843',
        clusterBkg: '#1E293B',
        titleColor: '#D4A843',
        edgeLabelBackground: '#070D1A',
        textColor: '#CBD5E1',
        labelTextColor: '#CBD5E1',
        nodeTextColor: '#F0EDE6',
    },
});

export default function MermaidChart({ chart, caption }) {
    const uniqueId = useId().replace(/:/g, '');
    const chartId = `mermaid-${uniqueId}`;
    const containerRef = useRef(null);
    const [svg, setSvg] = useState('');
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!chart?.trim()) return;
        let cancelled = false;

        (async () => {
            try {
                // Small delay lets the DOM stabilise
                await new Promise((r) => setTimeout(r, 60));
                if (cancelled) return;

                const { svg: rendered } = await mermaid.render(chartId, chart.trim());
                if (!cancelled) { setSvg(rendered); setReady(true); }
            } catch (err) {
                if (!cancelled) {
                    console.error('[MermaidChart] render error:', err);
                    setError('Chart could not be rendered.');
                    setReady(true);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [chart, chartId]);

    if (!ready) {
        return (
            <div className="ffa-mermaid-skeleton" aria-label="Loading chart…">
                <div className="ffa-skeleton-inner">
                    <span className="ffa-skeleton-label">Loading chart…</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ffa-mermaid-error">
                <span>⚠ {error}</span>
            </div>
        );
    }

    return (
        <figure className="ffa-mermaid-figure">
            <div
                ref={containerRef}
                className="ffa-mermaid-container"
                dangerouslySetInnerHTML={{ __html: svg }}
            />
            {caption && <figcaption className="ffa-mermaid-caption">{caption}</figcaption>}
        </figure>
    );
}
